import {Component, OnInit, TemplateRef} from '@angular/core';
import { HttpClient, HttpEventType } from '@angular/common/http';
import {DashboardService} from '../dashboard.service';
import {ToastrService} from 'ngx-toastr';
import {UtilsService} from '../../core/services/utils/utils.service';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {ImageCroppedEvent} from 'ngx-image-cropper';
import {createCroppedImageFile, getUploadProgress, isImageFile} from '../../core/services/utils/image-upload.utils';

@Component({
    selector: 'app-create-category',
    templateUrl: './create-category.component.html',
    styleUrls: ['./create-category.component.css'],
    standalone: false
})
export class CreateCategoryComponent implements OnInit {

  isCategoryCreated = false;
  isInfoParamsAdded = false;
  isSubParamsAdded = false;
  creatingCategory = false;
  savingCategory = false;
  savingInfo = false;
  savingSub = false;
  category: any = {
    categoryName: '',
    icon: '',
    status: 'Enabled',
    isFeatured: false,
    in_person: false,
    forceEntityToUseCategoryAllowedUserActions: false,
    allowedUserActions: {bhakt: true, unbhakt: true, rating: true, story: true, hypocrisy: true},
  };
  categoryId = '';
  icon = '';
  image = '';
  modalRef: BsModalRef;
  croppedFile: any;
  imageChangedEvent: any = '';
  aspectRatio = 1;
  uploadingIcon = false;
  uploadProgress = 0;

  subParamData = {subjective_fields: []};

  infoParamData = {informative_fields: []};

  addSubItemData = {id: '', fieldName: ''};

  addInfoItemData = {id: '', fieldName: '', fieldType: 'text', isRequired: false};

  constructor(
    private dashboardService: DashboardService,
    private utils: UtilsService,
    private toastr: ToastrService,
    private modalService: BsModalService,
    private httpClient: HttpClient
  ) {}

  ngOnInit(): void {
  }

  get hasCategoryId(): boolean {
    return !!this.categoryId;
  }

  private toBoolean(value: any): boolean {
    return value === true || value === 'true';
  }

  private normalizeCategory(data: any): any {
    const payload = data?.response || data?.data || data || {};
    return {
      ...this.category,
      ...payload,
      id: payload?.id || payload?._id || this.categoryId,
      isFeatured: this.toBoolean(payload?.isFeatured),
      forceEntityToUseCategoryAllowedUserActions: this.toBoolean(payload?.forceEntityToUseCategoryAllowedUserActions),
      allowedUserActions: {
        bhakt: true,
        unbhakt: true,
        rating: true,
        story: true,
        hypocrisy: true,
        ...(payload?.allowedUserActions || {})
      }
    };
  }

  goBack(): void {
    this.utils.goBack();
  }

  // Info Parameters
  addInfoItem(): void {
    this.infoParamData.informative_fields.push(this.addInfoItemData);
    this.addInfoItemData = {id: '', fieldName: '', fieldType: 'text', isRequired: false};
  }

  deleteInfoItem(index: number): void {
    this.infoParamData.informative_fields.splice(index, 1);
  }

  updateInfoParams(): void {
    if (!this.hasCategoryId) {
      this.utils.toastError('Save category first');
      return;
    }
    this.savingInfo = true;
    this.dashboardService.updateInformativeParam(this.infoParamData, this.categoryId).subscribe(res => {
      this.toastr.success('Info Params Updated');
      this.isInfoParamsAdded = true;
      this.savingInfo = false;
    }, err => {
      this.savingInfo = false;
      this.toastr.error(err.error?.message || 'Unable to update informative parameters');
    });
  }

  createCategory(): void {
    if (this.hasCategoryId) {
      this.updateCategory();
      return;
    }

    if (!this.category.icon || !this.category.categoryName) {
      this.utils.toastError('Category name and icon are required');
      return;
    }
    this.creatingCategory = true;
    this.dashboardService.createCategory(this.category).subscribe(res => {
      const normalized = this.normalizeCategory(res);
      this.category = normalized;
      this.categoryId = String(normalized?.id || normalized?._id || '');
      this.isCategoryCreated = true;
      this.creatingCategory = false;
      this.toastr.success('Category Created Successfully.');
      if (!this.categoryId) {
        this.utils.toastError('Category created but id was not returned. Please refresh and retry.');
      }
    }, err => {
      this.creatingCategory = false;
      this.utils.resErr(err);
    });
  }

  fileChangeEvent(event: any, template: TemplateRef<any>): void {
    const input = event?.target as HTMLInputElement;
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    if (!isImageFile(file)) {
      this.utils.toastError('Please select an image file only');
      input.value = '';
      return;
    }
    this.modalRef = this.modalService.show(template);
    this.aspectRatio = 1;
    this.croppedFile = null;
    this.imageChangedEvent = event;
  }

  imageCropped(event: ImageCroppedEvent): void {
    this.croppedFile = createCroppedImageFile(event);
  }

  loadImageFailed(): void {
    this.utils.toastError('Image Load failed!');
  }

  closeCropperModal(): void {
    if (this.uploadingIcon) {
      this.utils.toastError('Please wait for upload to complete');
      return;
    }
    if (this.modalRef) {
      this.modalRef.hide();
    }
    if (this.imageChangedEvent?.target) {
      this.imageChangedEvent.target.value = '';
    }
    this.imageChangedEvent = null;
    this.croppedFile = null;
    this.uploadingIcon = false;
    this.uploadProgress = 0;
  }

  uploadImage(): void {
    if (!this.croppedFile) {
      this.utils.toastError('Please crop the selected image');
      return;
    }
    this.uploadingIcon = true;
    const formData = new FormData();
    formData.append('file', this.croppedFile, this.croppedFile?.name || `upload-${Date.now()}.png`);
    const upload = this.httpClient.post<any>('file-upload', formData, {
      reportProgress: true,
      observe: 'events'
    });
    upload.subscribe(
      (res: any) => {
        if (res.type === HttpEventType.UploadProgress) {
          this.uploadProgress = getUploadProgress(res.loaded, res.total);
        } else if (res.type === HttpEventType.Response) {
          this.uploadingIcon = false;
          this.category.icon = res.body.response.url;
          this.uploadProgress = 0;
          this.closeCropperModal();
        }
      },
      (err) => {
        this.uploadingIcon = false;
        this.utils.resErr(err);
      }
    );
  }


  // Sub Parameters
  addSubItem(): void {
    this.subParamData.subjective_fields.push(this.addSubItemData);
    this.addSubItemData = {id: '', fieldName: ''};
  }

  deleteSubItem = (index: number) => this.subParamData.subjective_fields.splice(index, 1);

  updateSubParams(): void {
    if (!this.hasCategoryId) {
      this.utils.toastError('Save category first');
      return;
    }
    this.savingSub = true;
    this.dashboardService.updateSubjectiveParam(this.subParamData, this.categoryId).subscribe(res => {
      this.utils.toastSuccess('Subjective Params Updated');
      this.isSubParamsAdded = true;
      this.savingSub = false;
    }, err => {
      this.savingSub = false;
      this.toastr.error(err.error?.message || 'Unable to update subjective parameters');
    });
  }

  updateCategory(): void {
    if (!this.hasCategoryId) {
      this.utils.toastError('Save category first');
      return;
    }
    this.savingCategory = true;
    this.dashboardService.updateCategory(this.category, this.categoryId).subscribe(() => {
      this.utils.toastSuccess('Category Updated Successfully!');
      this.savingCategory = false;
    }, err => {
      this.savingCategory = false;
      this.utils.resErr(err);
    });
  }
}
